const { DataPacket_Kind } = require('livekit-client');
const { LKBase } = require('./lk-base');
const { LK_EVENTS } = require('./lk-events');

class LKLive extends LKBase {
  /**
   * Data channel module for real-time interactive features
   * (polls, reactions, quizzes, etc.). No media tracks.
   * @param {HTMLElement|null} container - null for headless usage
   * @param {object} options - LKBase options + live-specific:
   * @param {boolean} [options.reliable=true]  - Use reliable data channel
   * @param {Array<string>} [options.topics=[]] - Topics to listen to
   * @param {boolean} [options.renderReactions=false] - Show floating reactions overlay
   * @param {boolean} [options.showStatus=true] - Show connection status bar
   */
  constructor(container, options) {
    super(container, options);
    this._reliable = options.reliable !== false;
    this._topics = options.topics || [];
    this._renderReactions = options.renderReactions || false;
    this._showStatus = options.showStatus !== false;
    this._topicListeners = {};  // topic → [callback]
    this._statusEl = null;
  }

  // ─── Public API ──────────────────────────────────────────────────

  /**
   * Send data to other participants via data channel.
   * @param {string} topic - Message topic
   * @param {*} payload - Data to send (will be JSON-stringified)
   * @param {Array<string>} [destinationSids] - Target specific participants (empty = broadcast)
   */
  async sendData(topic, payload, destinationSids) {
    if (!this.room) throw new Error('Not connected');

    const message = JSON.stringify({ topic, payload });
    const data = new TextEncoder().encode(message);
    const kind = this._reliable
      ? DataPacket_Kind.RELIABLE
      : DataPacket_Kind.LOSSY;

    await this.room.localParticipant.publishData(data, { reliable: this._reliable, destinationSids, topic });

    this._emit(LK_EVENTS.DATA_SENT, { topic, payload });
  }

  /**
   * Register a listener for a specific topic.
   */
  onData(topic, callback) {
    if (!this._topicListeners[topic]) this._topicListeners[topic] = [];
    this._topicListeners[topic].push(callback);
    return this;
  }

  /**
   * Remove a topic listener.
   */
  offData(topic, callback) {
    const list = this._topicListeners[topic];
    if (!list) return this;
    const idx = list.indexOf(callback);
    if (idx !== -1) list.splice(idx, 1);
    return this;
  }

  /**
   * Convenience: broadcast a reaction emoji.
   */
  async broadcastReaction(emoji) {
    await this.sendData('reaction', { emoji, timestamp: Date.now() });
  }

  /**
   * Convenience: send a poll response.
   */
  async sendPollResponse(pollId, answer) {
    await this.sendData('poll:response', { pollId, answer, timestamp: Date.now() });
  }

  // ─── UI rendering ────────────────────────────────────────────────

  _renderUI() {
    if (!this.container) return;
    this.container.classList.add('lk-container', 'lk-live-mode');

    if (this._showStatus) {
      this._statusEl = document.createElement('div');
      this._statusEl.className = 'lk-live-status';
      this._updateStatus();
      this.container.appendChild(this._statusEl);
    }

    if (this._renderReactions) {
      this._reactionsEl = document.createElement('div');
      this._reactionsEl.className = 'lk-reactions-overlay';
      this.container.appendChild(this._reactionsEl);
    }
  }

  _cleanupUI() {
    this._statusEl = null;
    this._reactionsEl = null;
    this._topicListeners = {};

    if (this.container) {
      this.container.classList.remove('lk-container', 'lk-live-mode');
      this.container.innerHTML = '';
    }
  }

  _updateStatus() {
    if (!this._statusEl || !this.room) return;
    const count = this.getParticipantCount();
    this._statusEl.textContent = `Connected — ${count} participant${count !== 1 ? 's' : ''}`;
    this._statusEl.className = 'lk-live-status lk-live-status--connected';
  }

  _showReactionFloat(emoji, participantName) {
    if (!this._reactionsEl) return;
    const el = document.createElement('div');
    el.className = 'lk-reaction-float';
    el.textContent = emoji;
    el.title = participantName;
    // Random horizontal position
    el.style.left = (10 + Math.random() * 80) + '%';
    this._reactionsEl.appendChild(el);
    // Remove after animation
    setTimeout(() => el.remove(), 2000);
  }

  // ─── Room event hooks ────────────────────────────────────────────

  _onParticipantConnected(participant) {
    this._updateStatus();
  }

  _onParticipantDisconnected(participant) {
    this._updateStatus();
  }

  /**
   * Override the base DataReceived handler to dispatch by topic.
   */
  _setupRoomListeners() {
    super._setupRoomListeners();
    if (!this.room) return;

    // Override default data handler with topic-aware dispatch
    this.room.on('dataReceived', (payload, participant, kind, topic) => {
      try {
        const text = new TextDecoder().decode(payload);
        const msg = JSON.parse(text);
        const msgTopic = msg.topic || topic || 'unknown';
        const msgPayload = msg.payload;

        // Fire topic listeners
        const list = this._topicListeners[msgTopic];
        if (list) {
          for (const cb of list) {
            cb({ payload: msgPayload, participant, topic: msgTopic });
          }
        }

        // Handle reactions
        if (this._renderReactions && msgTopic === 'reaction' && msgPayload.emoji) {
          this._showReactionFloat(
            msgPayload.emoji,
            participant ? (participant.name || participant.identity) : 'Unknown'
          );
        }
      } catch (e) {
        // Non-JSON data, ignore
      }
    });
  }
}

module.exports = { LKLive };
